/**
 * Fonte unica da verdade (machine-readable) do tracking plan do Wings.
 *
 * Este arquivo e' o espelho em codigo de `docs/tracking-plan.md`. Toda
 * adicao/alteracao de evento DEVE atualizar os dois ao mesmo tempo. O tipo
 * `Record<AnalyticsEventName, EventSpec>` garante em tempo de compilacao que
 * nenhum evento de `events.ts` fica sem documentacao aqui.
 */
import { AnalyticsEventName } from './events';

/** Versao semantica do tracking plan. Bump a cada release que altera eventos. */
export const TRACKING_PLAN_VERSION = '1.1.2';

/** Tipos primitivos aceitos como property de evento. */
export type PropertyType = 'string' | 'number' | 'boolean';

/**
 * Estado de instrumentacao do evento:
 * - `active`: disparado por codigo de produto hoje.
 * - `auto`: emitido automaticamente pela stack (PostHog lifecycle / navigator).
 * - `planned`: catalogado no plano, ainda sem ponto de disparo no app.
 */
export type EventStatus = 'active' | 'auto' | 'planned';

export interface EventSpec {
  /** Descricao curta do gatilho do evento. */
  description: string;
  /** Properties documentadas e seus tipos. Ausente = evento sem properties. */
  properties?: Record<string, PropertyType>;
  /** Estado de instrumentacao. Default tratado como `active`. */
  status?: EventStatus;
  /**
   * Quando true, properties fora do schema sao aceitas sem warning. Usado em
   * eventos genericos (`cta_tap`, `error_shown`) que carregam extras variaveis.
   */
  allowAdditional?: boolean;
}

/**
 * Catalogo completo. A chave e' o NOME do evento (snake_case), nao a constante.
 * Mantenha agrupado e na mesma ordem de `events.ts` / `tracking-plan.md`.
 */
export const EVENT_SCHEMA: Record<AnalyticsEventName, EventSpec> = {
  // 1. Ciclo de vida do app
  app_open: { description: 'Boot do app apos Tracker.init', status: 'active' },
  app_background: { description: 'App foi para background (PostHog lifecycle)', status: 'auto' },
  screen_view: {
    description: 'Troca de rota (delegado a Tracker.screen via navigator)',
    status: 'auto',
    allowAdditional: true,
  },

  // 2. Onboarding / signup
  auth_signup_started: {
    description: 'Entrada na tela de SignUp',
    properties: { source: 'string' },
    status: 'active',
  },
  auth_signup_submitted: { description: 'Submit do formulario de cadastro valido', status: 'active' },
  auth_signup_failed: {
    description: 'Falha no POST /user/signup',
    properties: { reason_code: 'string' },
    status: 'active',
  },
  auth_signup_email_validation_sent: { description: 'Codigo de validacao enviado por email', status: 'active' },
  auth_signup_country_selected: {
    description: 'Pais escolhido em SignUpCountry',
    properties: { country_ddi: 'number' },
    status: 'active',
  },
  auth_signup_phone_submitted: {
    description: 'Telefone enviado ou pulado em SignUpPhone',
    properties: { country_ddi: 'number', phone_skipped: 'boolean' },
    status: 'active',
  },
  auth_signup_email_validated: { description: 'Codigo de email validado com sucesso', status: 'active' },
  auth_signup_email_validation_failed: {
    description: 'Codigo de email invalido',
    properties: { attempt_count: 'number' },
    status: 'active',
  },
  auth_signup_completed: { description: 'Signup concluido (primeira entrada na Home)', status: 'active' },

  // 3. Login
  auth_login_attempt: {
    description: 'Tentativa de login',
    properties: { method: 'string' },
    status: 'active',
  },
  auth_login_success: {
    description: 'Login bem-sucedido',
    properties: { method: 'string' },
    status: 'active',
  },
  auth_login_failure: {
    description: 'Login falhou',
    properties: { method: 'string', reason_code: 'string' },
    status: 'active',
  },
  auth_biometric_prompted: { description: 'Biometria apresentada ao usuario', status: 'active' },
  auth_biometric_success: { description: 'Biometria autenticada', status: 'active' },
  auth_biometric_failed: {
    description: 'Biometria recusada/falhou',
    properties: { reason_code: 'string' },
    status: 'active',
  },
  auth_biometric_activated: { description: 'Usuario ativou biometria', status: 'active' },
  auth_logout: { description: 'Logout no Profile', status: 'active' },

  // 4. Recuperacao de senha
  auth_forgot_password_started: {
    description: 'Entrada na tela ForgotPassword',
    properties: { source: 'string' },
    status: 'active',
  },
  auth_forgot_password_requested: { description: 'Email de recuperacao enviado', status: 'active' },
  auth_forgot_password_failed: {
    description: 'Falha no envio do email de recuperacao',
    properties: { reason_code: 'string' },
    status: 'active',
  },
  auth_password_reset_submitted: { description: 'Nova senha submetida', status: 'active' },
  auth_password_reset_completed: { description: 'Reset de senha concluido', status: 'active' },
  auth_password_change_started: { description: 'Tap em "Alterar senha" no Profile', status: 'active' },
  auth_password_change_completed: { description: 'Senha alterada com sucesso', status: 'active' },

  // 5. Criacao de ativos (funil principal de Growth)
  asset_create_started: {
    description: 'Tap em um tipo de ativo na AssetHandleScreen',
    properties: { asset_type: 'string', source: 'string' },
    status: 'active',
  },
  asset_form_step_completed: {
    description: 'Avancou de um step do formulario para o proximo',
    properties: { asset_type: 'string', step: 'string', step_index: 'number' },
    status: 'planned',
  },
  asset_create_completed: {
    description: 'Asset salvo com sucesso (SummaryForm submit)',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  asset_create_abandoned: {
    description: 'Usuario saiu sem completar a criacao',
    properties: { asset_type: 'string', last_step: 'string' },
    status: 'active',
  },
  asset_create_failed: {
    description: 'Erro no POST de criacao do ativo',
    properties: { asset_type: 'string', reason_code: 'string' },
    status: 'active',
  },

  // 6. Lifecycle de ativos existentes
  asset_details_viewed: {
    description: 'Abertura de uma tela de detalhes de ativo',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  asset_edit_started: {
    description: 'Tap em editar no detalhe do ativo',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  asset_edit_completed: {
    description: 'Edicao do ativo salva',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  asset_delete_started: {
    description: 'Tap em excluir ativo',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  asset_delete_confirmed: {
    description: 'Confirmou exclusao no alert',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  asset_delete_completed: {
    description: 'Exclusao concluida',
    properties: { asset_type: 'string' },
    status: 'active',
  },

  // 7. Transactions
  transaction_charge_started: { description: 'Entrada na ChargeScreen', status: 'active' },
  transaction_charge_submitted: {
    description: 'Submit do formulario de aporte',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  transaction_charge_completed: {
    description: 'Aporte registrado com sucesso',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  transaction_charge_failed: {
    description: 'Falha ao registrar aporte',
    properties: { asset_type: 'string', reason_code: 'string' },
    status: 'active',
  },
  transaction_withdraw_started: { description: 'Entrada na WithdrawScreen', status: 'active' },
  transaction_withdraw_submitted: {
    description: 'Submit do formulario de resgate',
    properties: { asset_type: 'string', is_total: 'boolean' },
    status: 'active',
  },
  transaction_withdraw_completed: {
    description: 'Resgate registrado com sucesso',
    properties: { asset_type: 'string' },
    status: 'active' },
  transaction_withdraw_failed: {
    description: 'Falha ao registrar resgate',
    properties: { asset_type: 'string', reason_code: 'string' },
    status: 'active',
  },
  transaction_custom_event_submitted: {
    description: 'Submit em CustomEventScreen',
    properties: { asset_type: 'string' },
    status: 'active',
  },
  transaction_custom_event_completed: {
    description: 'Evento customizado registrado',
    properties: { asset_type: 'string' },
    status: 'active',
  },

  // 8. Home / Wallet / Insights
  home_wallet_carousel_swiped: {
    description: 'Swipe no carousel da Home',
    properties: { from_index: 'number', to_index: 'number' },
    status: 'planned',
  },
  home_secure_value_toggled: {
    description: 'Toggle do botao de esconder valores',
    properties: { is_secure: 'boolean' },
    status: 'active',
  },
  home_goal_edit_opened: { description: 'Abriu edicao de meta', status: 'active' },
  home_goal_saved: { description: 'Meta salva', status: 'active' },
  home_goal_register_tapped: {
    description: 'CTA da meta vazia na Home para cadastrar meta de patrimonio',
    status: 'active',
  },
  home_empty_add_asset_tapped: {
    description: 'CTA da Home vazia para cadastrar o primeiro ativo',
    status: 'active',
  },

  onboarding_tour_shown: {
    description: 'Tour guiado de primeiro acesso exibido (coachmark na aba Ativos)',
    properties: { source: 'string' },
    status: 'active',
    allowAdditional: true,
  },
  onboarding_tour_started: {
    description: 'Usuario tocou em "Cadastrar agora" no tour guiado',
    status: 'active',
  },
  onboarding_tour_skipped: {
    description: 'Usuario dispensou o tour guiado de primeiro acesso',
    status: 'active',
  },
  onboarding_tour_replay_requested: {
    description: 'Usuario pediu para ver o tour novamente no Perfil',
    status: 'active',
  },

  // 9. Notificacoes
  notification_received_foreground: {
    description: 'Notificacao recebida com app aberto',
    properties: { type: 'string' },
    status: 'planned',
  },
  notification_opened: {
    description: 'Usuario abriu uma notificacao',
    properties: { type: 'string' },
    status: 'planned',
  },
  notification_marked_read: {
    description: 'Notificacoes marcadas como lidas',
    properties: { count: 'number' },
    status: 'planned',
  },

  // 10. Perfil do investidor (suitability)
  investor_profile_started: {
    description: 'Inicio do questionario de perfil do investidor',
    properties: { source: 'string' },
    status: 'active',
  },
  investor_profile_step_completed: {
    description: 'Avancou de um step do questionario',
    properties: { step: 'string', step_index: 'number' },
    status: 'planned',
  },
  investor_profile_completed: {
    description: 'Perfil do investidor calculado e salvo',
    properties: { profile_type: 'string' },
    status: 'active',
  },
  investor_profile_abandoned: {
    description: 'Saiu sem completar o questionario',
    properties: { last_step: 'string' },
    status: 'planned',
  },

  // 11. Perfil / Configuracoes / Privacidade
  profile_biometric_toggled: {
    description: 'Toggle de biometria no Profile',
    properties: { enabled: 'boolean' },
    status: 'active',
  },
  profile_account_delete_started: { description: 'Tap em "Remover minha conta"', status: 'active' },
  profile_account_delete_confirmed: { description: 'Confirmou remocao no alert', status: 'active' },
  profile_account_delete_completed: { description: 'Conta removida (DELETE /user)', status: 'active' },
  profile_external_link_opened: {
    description: 'Tap em link externo',
    properties: { target: 'string' },
    status: 'active',
  },
  consent_analytics_changed: {
    description: 'Usuario mudou o consentimento LGPD',
    properties: { granted: 'boolean' },
    status: 'active',
  },

  terms_status_requested: {
    description: 'GET /user/terms-status iniciado (gate pos-login)',
    status: 'active',
  },
  terms_status_failed: {
    description: 'Falha ao verificar status dos termos (gate bloqueou a Home)',
    properties: { reason_code: 'string', attempt_count: 'number' },
    status: 'active',
  },
  terms_modal_shown: {
    description: 'Modal de termos/privacidade exibido ao usuario',
    properties: { requires_update: 'boolean' },
    status: 'active',
  },

  // 12. CTAs e erros transversais
  cta_tap: {
    description: 'Tap em qualquer Button com trackingId',
    properties: { cta_id: 'string', screen: 'string' },
    status: 'active',
    allowAdditional: true,
  },
  error_shown: {
    description: 'Toast de erro mostrado ao usuario',
    properties: { screen: 'string', code: 'string' },
    status: 'active',
    allowAdditional: true,
  },
  error_request_failed: {
    description: 'Falha HTTP capturada na camada de rede',
    properties: { endpoint: 'string', status_code: 'number', method: 'string' },
    status: 'active',
  },

  // 13. Analises (portal web)
  web_analysis_viewed: {
    description: 'Tela de analise carregada com sucesso',
    properties: { analysis_type: 'string', source: 'string' },
    status: 'active',
  },
  web_analysis_nav_tapped: {
    description: 'Tap em item do submenu Analises no side menu',
    properties: { analysis_type: 'string' },
    status: 'active',
  },
  web_analysis_menu_opened: {
    description: 'Submenu Analises expandido no side menu',
    status: 'active',
  },
  web_analysis_exported: {
    description: 'Exportacao PDF de uma tela de analise',
    properties: { analysis_type: 'string' },
    status: 'active',
  },
  web_analysis_period_changed: {
    description: 'Periodo alterado em tela de analise',
    properties: { analysis_type: 'string', preset: 'string' },
    status: 'active',
  },
  web_analysis_filter_applied: {
    description: 'Filtro aplicado em tela de analise',
    properties: { analysis_type: 'string', filter_name: 'string' },
    status: 'active',
  },
  web_analysis_group_by_changed: {
    description: 'Agrupamento alterado na rentabilidade',
    properties: { analysis_type: 'string', group_by: 'string' },
    status: 'active',
  },
};

/**
 * Valida um evento contra o `EVENT_SCHEMA`. Retorna a lista de mensagens de
 * problema (vazia quando tudo certo). Nao lanca.
 */
export function validateEvent(
  event: string,
  properties?: Record<string, unknown>,
): string[] {
  const warnings: string[] = [];
  const spec = (EVENT_SCHEMA as Record<string, EventSpec | undefined>)[event];

  if (!spec) {
    warnings.push(
      `evento '${event}' nao esta no EVENT_SCHEMA. Adicione em event-schema.ts e em docs/tracking-plan.md.`,
    );
    return warnings;
  }

  if (!properties) return warnings;

  for (const [key, value] of Object.entries(properties)) {
    if (value == null) continue;

    const expected = spec.properties?.[key];
    if (!expected) {
      if (!spec.allowAdditional) {
        warnings.push(`property '${key}' nao documentada para '${event}'.`);
      }
      continue;
    }

    const actual = Array.isArray(value) ? 'array' : typeof value;
    if (actual !== expected) {
      warnings.push(
        `property '${key}' de '${event}' esperava ${expected}, recebeu ${actual}.`,
      );
    }
  }

  return warnings;
}
